import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import ReimaginedContributionHistory from 'tio-employee/components/reimagined/production/contribution-history';
import LoanStandingAlertCard from 'tio-employee/components/slr/loan-standing-alert-card';
import LoanStandingUploadDrawer from 'tio-employee/components/slr/loan-standing-upload-drawer';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;

  @tracked loanStandingVerification = this.args.model.loanStandingVerification;
  @tracked isLoanStandingUploadDrawerOpen = false;

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  get showLoanStandingAlert() {
    return shouldShowSlrLoanStandingAlert(this.loanStandingVerification);
  }

  openLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = true;
  };

  closeLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = false;
  };

  refreshLoanStandingVerification = dropTask(async () => {
    const personId = this.args.model.person.id;
    if (!personId) {
      throw new Error('Cannot refresh SLR loan standing without a person id.');
    }

    this.loanStandingVerification =
      await this.slrLoanStandingVerification.fetchCurrentState(personId);
  });

  <template>
    <div class="flex flex-col gap-2">
      <h1 class="app-page-title px-4 pt-4">{{t "dashboard_tiles.student_loan_repayment"}}</h1>
      {{#if this.loanStandingVerification}}
        {{#if this.showLoanStandingAlert}}
          <div class="px-4">
            <LoanStandingAlertCard
              @state={{this.loanStandingVerification}}
              @onOpenUpload={{this.openLoanStandingUploadDrawer}}
            />
          </div>
        {{/if}}
      {{/if}}
      <ReimaginedContributionHistory @model={{this.reimaginedModel}} />
    </div>

    {{#if this.loanStandingVerification}}
      <LoanStandingUploadDrawer
        @isOpen={{this.isLoanStandingUploadDrawerOpen}}
        @state={{this.loanStandingVerification}}
        @onClose={{this.closeLoanStandingUploadDrawer}}
        @onRefresh={{this.refreshLoanStandingVerification.perform}}
      />
    {{/if}}
  </template>
}
